import { PROTOCOL_VERSION, TICK_RATE } from '../constants';
import type { ModeId } from '../game/types';
import { BinaryReader, BinaryWriter } from './codec';
import { decodeTick, decodeWelcome, encodeEvents, encodeWelcome, Ev, S2C, type GameEvent } from './protocol';

/**
 * Highlight replays: a stretch of a match as the server broadcast it, playable by the normal game
 * client. A replay starts with a keyframe (the messages a player joining at that moment gets: welcome,
 * changed chunks, chunks-done) followed by every broadcast message (spectator ticks without chat,
 * player info, scoreboard...) stamped with its server tick.
 */

export type HighlightKind = 'multikill' | 'collapse' | 'final' | 'destruction';

export interface HighlightMoment {
  kind: HighlightKind;
  /** Short title ("Triple takedown"). */
  label: string;
  score: number;
  /** In-match id of the featured player (-1 = none). */
  subject: number;
  /** First and last tick of the moment itself; the clip adds lead-in and follow-through. */
  fromTick: number;
  toTick: number;
  /** Where it happened (center of the orbit camera). */
  x: number;
  y: number;
  z: number;
  kills: number;
}

export interface ReplayHeader {
  version: number;
  protocol: number;
  matchId: string;
  mode: ModeId;
  mapId: string;
  tickRate: number;
  /** Tick of the keyframe; messages run to endTick. */
  startTick: number;
  endTick: number;
  moment: HighlightMoment;
  createdAt: string;
}

export interface ReplayMessage {
  tick: number;
  data: Uint8Array;
}

export interface Replay {
  header: ReplayHeader;
  keyframe: Uint8Array[];
  messages: ReplayMessage[];
}

const MAGIC = 0x50524d56; // 'VMRP'
export const REPLAY_VERSION = 1;

export function encodeReplay(r: Replay): Uint8Array {
  const size = 64 + r.keyframe.reduce((a, d) => a + d.length + 5, 0) + r.messages.reduce((a, m) => a + m.data.length + 9, 0);
  const w = new BinaryWriter(size);
  w.u32(MAGIC).u16(REPLAY_VERSION).string(JSON.stringify(r.header));
  w.u32(r.keyframe.length);
  for (const d of r.keyframe) w.blob(d);
  w.u32(r.messages.length);
  for (const m of r.messages) w.u32(m.tick).blob(m.data);
  return w.finish();
}

export function decodeReplay(bytes: Uint8Array): Replay {
  const r = new BinaryReader(bytes);
  if (r.u32() !== MAGIC) throw new Error('not a Voxel Marines replay');
  const version = r.u16();
  if (version !== REPLAY_VERSION) throw new Error(`unsupported replay version ${version}`);
  const header = JSON.parse(r.string()) as ReplayHeader;
  const keyframe: Uint8Array[] = [];
  for (let n = r.u32(); n > 0; n--) keyframe.push(r.blob().slice());
  const messages: ReplayMessage[] = [];
  for (let n = r.u32(); n > 0; n--) {
    const tick = r.u32();
    messages.push({ tick, data: r.blob().slice() });
  }
  return { header, keyframe, messages };
}

export function newReplayHeader(h: Omit<ReplayHeader, 'version' | 'protocol' | 'tickRate'>): ReplayHeader {
  return { version: REPLAY_VERSION, protocol: PROTOCOL_VERSION, tickRate: TICK_RATE, ...h };
}

/** Replay length in seconds. */
export function replayDuration(h: ReplayHeader): number {
  return (h.endTick - h.startTick) / h.tickRate;
}

// Sharing outside the staff console --------------------------------------------------------------

/**
 * A copy of a replay without anything that identifies players, for public links, the website and
 * players' own clips: human players become "Marine N" (bots keep their made-up names), account ids and
 * clan tags go, and names inside announcements ("Ada captured the flag!") are replaced too. Chat was
 * never recorded. Running it again changes nothing.
 */
export function anonymizeReplay(r: Replay): Replay {
  const names = new Map<string, string>();
  const keyframe = r.keyframe.map((d) => anonymizeMessage(d, names));
  const messages = r.messages.map((m) => ({ tick: m.tick, data: anonymizeMessage(m.data, names) }));
  return { header: r.header, keyframe, messages };
}

function anonymizeMessage(data: Uint8Array, names: Map<string, string>): Uint8Array {
  const type = data[0];
  if (type === S2C.Welcome) {
    const r = new BinaryReader(data);
    r.u8();
    const w = decodeWelcome(r);
    return encodeWelcome({ ...w, json: scrubJson(w.json, names) });
  }
  if (type === S2C.PlayerInfo || type === S2C.Scoreboard || type === S2C.MatchEnd) {
    const r = new BinaryReader(data);
    r.u8();
    const json = scrubJson(r.string(), names);
    return new BinaryWriter(json.length * 3 + 16).u8(type).string(json).finish();
  }
  if (type === S2C.Tick && names.size) return scrubTick(data, names);
  return data;
}

function scrubJson(json: string, names: Map<string, string>): string {
  const v: unknown = JSON.parse(json);
  scrubValue(v, names);
  return JSON.stringify(v);
}

/** Player records (info, score lines, results) all carry `isBot`; humans get an alias. */
function scrubValue(v: unknown, names: Map<string, string>): void {
  if (Array.isArray(v)) {
    for (const x of v) scrubValue(x, names);
    return;
  }
  if (!v || typeof v !== 'object') return;
  const o = v as Record<string, unknown>;
  if (typeof o.name === 'string' && o.isBot === false) {
    const id = typeof o.id === 'number' ? o.id : typeof o.playerId === 'number' ? o.playerId : -1;
    let alias = names.get(o.name);
    if (!alias) {
      alias = `Marine ${id >= 0 ? id + 1 : names.size + 1}`;
      names.set(o.name, alias);
    }
    o.name = alias;
  }
  delete o.userId;
  delete o.clanTag;
  for (const k of Object.keys(o)) scrubValue(o[k], names);
}

function replaceNames(text: string, names: Map<string, string>, json: boolean): string {
  // Longest first, so "Ada" doesn't break up "Adam".
  for (const [real, alias] of [...names].sort((a, b) => b[0].length - a[0].length)) {
    if (real === alias) continue;
    const needle = json ? JSON.stringify(real).slice(1, -1) : real;
    if (needle) text = text.split(needle).join(alias);
  }
  return text;
}

/** Ticks only change when they carry text (announcements, mode JSON); their event block is re-encoded. */
function scrubTick(data: Uint8Array, names: Map<string, string>): Uint8Array {
  const r = new BinaryReader(data);
  r.u8();
  const t = decodeTick(r);
  if (!t.events.some((e) => e.e === Ev.Announce || e.e === Ev.Mode || e.e === Ev.Chat)) return data;
  // Events are the end of a tick message: find where they start by encoding them again.
  const original = encodeEvents(t.events);
  const start = data.length - original.length;
  if (start <= 0 || !data.subarray(start).every((b, i) => b === original[i])) throw new Error('replay tick could not be re-encoded');
  const events: GameEvent[] = [];
  for (const e of t.events) {
    if (e.e === Ev.Chat) continue;
    if (e.e === Ev.Announce) events.push({ ...e, text: replaceNames(e.text, names, false) });
    else if (e.e === Ev.Mode) events.push({ ...e, json: replaceNames(e.json, names, true) });
    else events.push(e);
  }
  const ev = encodeEvents(events);
  const out = new Uint8Array(start + ev.length);
  out.set(data.subarray(0, start));
  out.set(ev, start);
  return out;
}

// Replay studio protocol: the admin console embeds the game's replay page (replay.html) and drives it
// with postMessage.

/**
 * chase: behind the featured player; orbit: a slow sweep around the spot; drone: a cinematic push-in
 * past the spot; pov: through the featured player's eyes, with their weapon.
 */
export type ReplayCameraMode = 'chase' | 'orbit' | 'drone' | 'pov';
export type ClipFormat = 'mp4' | 'gif';
/** Playback speed: normal, half, quarter, or slow motion only around the moment itself. */
export type ClipSpeed = 'normal' | 'half' | 'quarter' | 'moment';

export interface ClipRequest {
  format: ClipFormat;
  /** Output size in pixels (even numbers for MP4). */
  width: number;
  height: number;
  fps: number;
  /** Seconds from the replay start. */
  from: number;
  to: number;
  camera: ReplayCameraMode;
  speed?: ClipSpeed;
  /** Logo in the corner and an end card with the game's address (default on). */
  brand?: boolean;
}

/** One highlight of a montage (seconds from that replay's start; -1 for a tight cut around the moment). */
export interface MontageItem {
  gzip: ArrayBuffer;
  from: number;
  to: number;
  camera: ReplayCameraMode;
  speed?: ClipSpeed;
  /** Title card shown before the clip ("#3 Triple takedown"); empty for none. */
  title: string;
}

/** Several highlights in one video: an opening card, each clip with its title card, the end card. */
export interface MontageRequest {
  format: ClipFormat;
  width: number;
  height: number;
  fps: number;
  /** Opening card ("Top plays of the week"); empty for none. */
  title: string;
  brand: boolean;
  items: MontageItem[];
}

export type ReplayCommand =
  | { t: 'load'; gzip: ArrayBuffer }
  | { t: 'play' }
  | { t: 'pause' }
  | { t: 'seek'; sec: number }
  | { t: 'camera'; mode: ReplayCameraMode }
  | { t: 'speed'; speed: ClipSpeed }
  | { t: 'export'; clip: ClipRequest }
  | { t: 'montage'; montage: MontageRequest }
  /** A still of the current frame (JPEG), for link previews and the website. */
  | { t: 'poster'; width: number; height: number }
  | { t: 'cancel' };

export type ReplayEvent =
  | { t: 'ready' }
  | { t: 'loaded'; header: ReplayHeader; duration: number }
  | { t: 'time'; sec: number; playing: boolean }
  | { t: 'progress'; done: number; total: number }
  | { t: 'exported'; blob: Blob; filename: string }
  | { t: 'poster'; blob: Blob }
  | { t: 'error'; message: string };
